
export interface AuraPointsData {
  totalPoints: number;
  level: number;
  ratingsReceived: number;
  ratingsGiven: number;
  averageRating: number;
  lastUpdated: string;
  history: AuraPointsHistoryItem[];
  achievements: Achievement[];
  streak: StreakInfo;
}

export interface AuraPointsHistoryItem {
  id: string;
  timestamp: string;
  points: number;
  reason: AuraPointsReason;
  details?: string;
}

export enum AuraPointsReason {
  RATING_RECEIVED = 'rating_received',
  RATING_GIVEN = 'rating_given',
  ENGAGEMENT = 'engagement',
  DAILY_BONUS = 'daily_bonus',
  PROFILE_COMPLETION = 'profile_completion',
  ACHIEVEMENT_UNLOCKED = 'achievement_unlocked',
  STREAK_BONUS = 'streak_bonus',
  OTHER = 'other'
}

export interface UserRating {
  fromUserId: string;
  toUserId: string;
  rating: number; // 1-5 stars
  timestamp: string;
  sessionId: string;
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlockedAt: string | null;
  pointsAwarded: number;
  progress?: {
    current: number;
    target: number;
  };
}

export interface StreakInfo {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
}

// These are the levels that users can achieve based on points
export const AURA_LEVELS = [
  { level: 1, name: "Novice", minPoints: 0, color: "#6E7582" },
  { level: 2, name: "Apprentice", minPoints: 100, color: "#3B82F6" },
  { level: 3, name: "Journeyman", minPoints: 300, color: "#10B981" },
  { level: 4, name: "Expert", minPoints: 600, color: "#8B5CF6" },
  { level: 5, name: "Master", minPoints: 1000, color: "#F59E0B" },
  { level: 6, name: "Grand Master", minPoints: 2000, color: "#EF4444" },
  { level: 7, name: "Legend", minPoints: 5000, color: "#EC4899" }
];

// Default achievements
export const DEFAULT_ACHIEVEMENTS: Achievement[] = [
  {
    id: "first_chat",
    name: "First Connection",
    description: "Complete your first chat session",
    icon: "message-circle",
    unlockedAt: null,
    pointsAwarded: 10
  },
  {
    id: "five_star",
    name: "Five-Star Communicator",
    description: "Receive ten 5-star ratings",
    icon: "star",
    unlockedAt: null,
    pointsAwarded: 50,
    progress: {
      current: 0,
      target: 10
    }
  },
  {
    id: "chat_marathon",
    name: "Chat Marathon",
    description: "Stay in a single chat for over 10 minutes",
    icon: "timer",
    unlockedAt: null,
    pointsAwarded: 30
  },
  {
    id: "social_butterfly",
    name: "Social Butterfly",
    description: "Chat with 20 different people",
    icon: "users",
    unlockedAt: null,
    pointsAwarded: 40,
    progress: {
      current: 0,
      target: 20
    }
  },
  {
    id: "profile_complete",
    name: "Identity Established",
    description: "Complete your profile information",
    icon: "user-check",
    unlockedAt: null,
    pointsAwarded: 15
  },
  {
    id: "week_streak",
    name: "Weekly Dedication",
    description: "Use the app for 7 consecutive days",
    icon: "calendar",
    unlockedAt: null,
    pointsAwarded: 70,
    progress: {
      current: 0,
      target: 7
    }
  }
];

// Helper functions
export function getLevelInfo(points: number) {
  const level = AURA_LEVELS.slice().reverse().find(l => points >= l.minPoints) || AURA_LEVELS[0];
  const nextLevel = AURA_LEVELS.find(l => l.minPoints > points);
  
  const progress = nextLevel 
    ? { current: points - level.minPoints, target: nextLevel.minPoints - level.minPoints } 
    : { current: 1, target: 1 }; // Max level
  
  return {
    ...level,
    progress,
    nextLevel: nextLevel ? nextLevel.name : null,
    pointsToNextLevel: nextLevel ? nextLevel.minPoints - points : 0
  };
}
